import { CSSProperties, FC, useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import useMediaQuery from "@mui/material/useMediaQuery";
import { negativeState, positiveState, regionState, themeState } from "@/lib/store";
import ContentLayout from "@/layouts/content/ContentLayout";
import { getPulseSignals } from "@/controllers/pulse";
import { loadMIContent } from "@/controllers/market-intelligence";
import { MIContent } from "@/models/market-intelligence";
import useModalLink from "src/hooks/useModalLink";
import { PulseCategory } from "@/models/pulse";
import styles from "./MarketsPulse.module.scss";
import DetailDialog, { DetailContent } from "./DetailDialog";
import IndexPanel from "./IndexPanel";
import { articleDetail, preloadArticleMarkdown } from "./details";
import { CategoryPanel, EarningsPanel, MacroPanel, OutlierTab, OutlierTabs, ResearchPanel, ShortMoversPanel, TopSignals, useOpenItem } from "./Panels";
import PulseTape from "./PulseTape";
import WhenNear from "./WhenNear";
import { usePulseData } from "./usePulseData";
import { usePanelOrder } from "./usePanelOrder";
import { LOWER_ROW_WAIT_MS, LowerRowMembers, MARKETS, RANKED_PANELS, RankedPanel, lowerRowReady, marketClock, nextLowerRowMembers, presentPanels, rankPanels, sessionMode } from "./utils";

type LowerPanelsProps = { universeId: number; today: RankedPanel[] | null; present: RankedPanel[] | null; fit: boolean; wide: boolean; outlierFirst: OutlierTab; onOpen: (content: DetailContent) => void };

/**
 * One panel per dataset, then earnings, alt data and macro. Mounted inside `WhenNear`: in a browser
 * after hydration, in a crawler's full render on the server. The panels' order is settled here,
 * once, before they are first drawn (see `usePanelOrder`), and so is which of the ranked panels are
 * drawn at all (`present`, from today's top signals): a panel drawn with a skeleton and then taken
 * away when its list came back empty moved its neighbours sideways (#1356). Null (not known: a
 * crawler's render, or the feed failed): every panel, which steps aside on its own if empty. Read
 * again for a new region once its feed is in (`nextLowerRowMembers`).
 */
const LowerPanels: FC<LowerPanelsProps> = ({ universeId, today, present, fit, wide, outlierFirst, onOpen }) => {
    const order = usePanelOrder(today);
    const [members, setMembers] = useState<LowerRowMembers>(() => ({ universeId, panels: present ?? RANKED_PANELS }));
    const next = nextLowerRowMembers(members, universeId, present);
    if (next !== members) setMembers(next);
    const has = (panel: RankedPanel) => next.panels.includes(panel);
    return (
        <>
            {has("shorts") && <ShortMoversPanel universeId={universeId} order={order.shorts} onOpen={onOpen} />}
            {has("insiders") && (
                <CategoryPanel
                    category="insiders"
                    title="Insider buying"
                    info="Open-market buying by company insiders in the last 7 days, scored on size, seniority and how many insiders are buying together."
                    universeId={universeId}
                    order={order.insiders}
                    onOpen={onOpen}
                />
            )}
            {has("gov") && universeId === 7 && (
                <CategoryPanel category="gov" title="US gov trades" info="Trades by members of Congress, the executive branch and the judiciary, disclosed in the last few days." universeId={universeId} order={order.gov} onOpen={onOpen} />
            )}
            {has("options") && <CategoryPanel category="options" title="Options flow" info="Names with the most unusual option premium today, and whether it leans to calls or puts." universeId={universeId} order={order.options} onOpen={onOpen} />}
            {!fit && (
                <>
                    <EarningsPanel universeId={universeId} onOpen={onOpen} />
                    <CategoryPanel
                        category="alt"
                        title="Alt data outliers"
                        info="Alternative data that is new and unusual: readings at least 2σ from their own history, and fresh records, streaks and year-on-year moves. At most three rows per type of data."
                        universeId={universeId}
                        order={15}
                        onOpen={onOpen}
                    />
                    <MacroPanel onOpen={onOpen} />
                </>
            )}
            {fit && !wide && <OutlierTabs tabs={["earnings", "alt", "macro"]} universeId={universeId} onOpen={onOpen} />}
            {wide && (
                <>
                    <EarningsPanel universeId={universeId} onOpen={onOpen} />
                    <OutlierTabs tabs={["alt", "macro"]} initial={outlierFirst} universeId={universeId} onOpen={onOpen} />
                </>
            )}
        </>
    );
};

/**
 * The front page: what is unusual right now across ORTEX's datasets.
 *
 * Reading order: the market (indexes, breadth, movers), then the biggest signals
 * across every dataset, then research. Below, one panel per dataset, ordered by how
 * unusual its top item is, so the eye lands on the most interesting first. The order is
 * settled before the panels are drawn and holds while they are on screen (`usePanelOrder`).
 * Panels with nothing to say render nothing and the row closes up.
 */
const MarketsPulse: FC = () => {
    const theme = useAtomValue(themeState);
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);
    const region = useAtomValue(regionState);
    const universeId = region === 10 ? 10 : 7;
    const [detail, setDetail] = useState<DetailContent | null>(null);
    // The page is pre-rendered at build time, so the clocks and the session can only be worked out
    // in the browser: null until mounted, then refreshed every 30 s. Rendering them on the server put
    // the build's time in the HTML, which never matched the reader's and failed hydration.
    const [now, setNow] = useState<Date | null>(null);
    const session = useMemo(() => (now ? sessionMode(now) : null), [now]);

    useEffect(() => {
        setNow(new Date());
        const timer = setInterval(() => setNow(new Date()), 30_000);
        return () => clearInterval(timer);
    }, []);

    // One item per dataset: today's ranking of the panels below. Tagged with its region, so a ranking
    // still on hand from the region before is never taken for this one's.
    const { data: tops } = usePulseData((signal) => getPulseSignals({ limit: 8, perCategory: 1, universeId }, signal).then((data) => ({ ...data, universeId })), [universeId], { refreshMs: 5 * 60_000 });
    const today = useMemo(() => (tops && tops.universeId === universeId ? rankPanels(tops.rows || []) : null), [tops, universeId]);
    // The same feed says which ranked panels have rows today; the lower row waits for it (at most
    // LOWER_ROW_WAIT_MS) so that no panel is drawn and then taken away (#1356).
    const present = useMemo(() => (tops && tops.universeId === universeId ? presentPanels(tops) : null), [tops, universeId]);
    const [waited, setWaited] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setWaited(true), LOWER_ROW_WAIT_MS);
        return () => clearTimeout(timer);
    }, []);
    const holdLowerRow = !lowerRowReady(present, waited ? LOWER_ROW_WAIT_MS : 0);

    // Which of alt data and macro is more unusual today: the tab that opens first.
    const outlierFirst: OutlierTab = useMemo(() => {
        const rows = tops?.rows || [];
        const score = (category: PulseCategory) => {
            const item = rows.find((row) => row.category === category);
            return item ? (item.interest ?? item.score) : -1;
        };
        return score("macro") > score("alt") ? "macro" : "alt";
    }, [tops]);

    // Desktop layouts fit one screen; below 1700px earnings, alt data and macro share one
    // tabbed column, above it earnings gets its own and alt data / macro share the last.
    const fit = useMediaQuery("(min-width:1280px) and (min-height:700px)");
    const wide = useMediaQuery("(min-width:1700px) and (min-height:700px)");

    const openItem = useOpenItem(setDetail);
    // Research clicks go to Market Intelligence; this keeps links already shared as /?show=article&id=<content id> working.
    const article = useModalLink<MIContent>("article", (id, signal) => loadMIContent(id, signal));
    useEffect(() => {
        if (article.id) preloadArticleMarkdown();
    }, [article.id]);
    const shown = article.item ? articleDetail(article.item) : detail;
    const closeDetail = () => {
        setDetail(null);
        article.close();
    };
    // The reader's own up/down colours (Settings), not fixed green and red.
    const colours = { "--mp-up": positive, "--mp-down": negative } as CSSProperties;

    return (
        <ContentLayout title="Market Pulse: Short Interest, Options and Signals" help={{ moduleId: "market_overview", modulePage: "V2_market_overview" }}>
            <>
                <div className={`${styles.MarketsPulse} ${theme === "light" ? styles.light : ""}`} style={positive && negative ? colours : undefined}>
                    <div className={styles.sessionBar}>
                        <span className={styles.live}>
                            <i />
                            LIVE
                        </span>
                        <span className={styles.sessionLabel}>
                            {session ? `${session.label} · ` : ""}
                            {universeId === 10 ? "Europe" : "US & Canada"}
                        </span>
                        <span className={styles.spacer} />
                        <div className={styles.clocks} role="group" aria-label="Market hours">
                            {MARKETS.map((market) => {
                                const clock = now ? marketClock(market, now) : null;
                                return (
                                    <span key={market.name} className={`${styles.clock} ${clock?.open ? styles.clockOpen : ""}`} title={clock ? (clock.open ? `${market.name} is open` : `${market.name} is closed`) : market.name}>
                                        <i />
                                        {market.name}
                                        {/* Until mounted, a hidden 00:00 holds the width of the time (tabular figures), so the pills do not move when it arrives. */}
                                        <b className={styles.num} style={clock ? undefined : { visibility: "hidden" }}>
                                            {clock ? clock.time : "00:00"}
                                        </b>
                                    </span>
                                );
                            })}
                        </div>
                    </div>

                    <PulseTape universeId={universeId} onOpen={openItem} />

                    <div className={styles.board}>
                        <div className={styles.rowA}>
                            <IndexPanel mode={session?.mode ?? "us"} universeId={universeId} />
                            <TopSignals universeId={universeId} onOpen={setDetail} />
                            <ResearchPanel />
                        </div>

                        {/* Left out of a browser's server HTML and drawn after hydration: once the browser is idle on
                            the one-screen desktop layout, as it nears the viewport on phones and small windows.
                            A crawler's full render has it in the HTML (src/lib/home-render.ts). */}
                        <WhenNear className={styles.rowB} placeholderClassName={styles.rowBWaiting} eager={fit} hold={holdLowerRow}>
                            <LowerPanels universeId={universeId} today={today} present={present} fit={fit} wide={wide} outlierFirst={outlierFirst} onOpen={setDetail} />
                        </WhenNear>
                    </div>
                </div>
                <DetailDialog content={shown} shareTitle={article.linked ? article.item?.headline : undefined} onClose={closeDetail} onLeave={() => setDetail(null)} />
            </>
        </ContentLayout>
    );
};

export default MarketsPulse;
